import { computed, map, type ReadableAtom } from 'nanostores';

import type { SyncClient } from '../../sync/client/sync_client.js';
import { syncAtom } from '../../sync/sync_atom.js';
import type { NewPlaylistItem, PlaylistItem } from '../../types.js';
import { getPlaylistItemWithDefaults } from '../../utils/get_playlist_item_with_defaults.js';

export type CompareMode = 'sideBySide' | 'overlay' | 'wipe';

export type CompareContentTypes = 'video' | 'image' | 'pdf' | 'stream';

export type Wipe = {
  x: number; // [0..1] normalized coordinate
  y: number; // [0..1] normalized coordinate
  angle: number; // [0..1] normalized angle (0.0 = 0°, 0.25 = 90°, 0.5 = 180°, 0.75 = 270°)
};

type ComparisonItems<PlaylistItemMeta> = {
  [id: string]: PlaylistItem<PlaylistItemMeta>;
};

export function createCompareStore<PlaylistItemMeta>(
  syncClient: SyncClient,
  $isLive: ReadableAtom<boolean>,
) {
  const $compareActive = syncAtom<boolean>('compareActive', false, {
    syncClient,
  });

  function toggleCompareActive() {
    const compareActive = $compareActive.get();
    $compareActive.set(!compareActive);
  }

  function setCompareActive(active: boolean) {
    $compareActive.set(active);
  }

  const $comparisonItems = map<ComparisonItems<PlaylistItemMeta>>();

  function setComparisonItems(
    playlistItems: NewPlaylistItem<PlaylistItemMeta>[],
  ) {
    const playlistItemsWithDefaults = playlistItems.map(
      getPlaylistItemWithDefaults,
    );
    const comparisonItemsCollection = Object.fromEntries(
      playlistItemsWithDefaults.map((item) => [item.id, item]),
    );
    $comparisonItems.set(comparisonItemsCollection);

    let newCompareItemId;
    if (playlistItemsWithDefaults.length > 0) {
      const id = $compareItemId.get();
      if (!id || !comparisonItemsCollection[id]) {
        newCompareItemId = playlistItemsWithDefaults[0].id;
      }
    }
    if (newCompareItemId) {
      $compareItemId.set(newCompareItemId);
    }
  }

  // TODO make compareAvailable not computed but set on user side.
  //  For example, user might want to lazy load comparison items once the compare mode becomes active,
  //  currently there's no way to make compare available without setting comparison items.
  const $compareAvailable = computed(
    [$comparisonItems, $isLive],
    (items, isLive) => !isLive && Object.keys(items).length > 0,
  );
  const $compareItemId = syncAtom<string | undefined>(
    'compareItemId',
    undefined,
    {
      syncClient,
    },
  );

  const $compareItem = computed(
    [$comparisonItems, $compareItemId, $compareActive],
    (items, id, compareActive) => {
      if (!id || !compareActive) {
        return undefined;
      }
      return items[id];
    },
  );

  function setCompareItemId(id: string | undefined) {
    $compareItemId.set(id);
  }

  const $compareMode = syncAtom<CompareMode>('compareMode', 'sideBySide', {
    syncClient,
  });

  function setCompareMode(mode: CompareMode) {
    $compareMode.set(mode);
  }

  const $overlayOpacity = syncAtom<number>('overlayOpacity', 0.5, {
    syncClient,
  });

  function setOverlayOpacity(opacity: number) {
    $overlayOpacity.set(opacity);
  }

  const $wipe = syncAtom<Wipe>(
    'wipe',
    { x: 0.5, y: 0.5, angle: 0.75 }, // 0.75 = 270° (South)
    {
      syncClient,
    },
  );

  function setWipe({ x, y, angle }: Partial<Wipe>) {
    const currentWipe = $wipe.get();
    const newWipe = {
      x: x ?? currentWipe.x,
      y: y ?? currentWipe.y,
      angle: angle ?? currentWipe.angle,
    };
    $wipe.set(newWipe);
  }

  return {
    $compareActive,
    $compareItem,
    $compareMode,
    $comparisonItems,
    $overlayOpacity,
    $wipe,
    $compareAvailable,
    toggleCompareActive,
    setCompareActive,
    setComparisonItems,
    setCompareItemId,
    setCompareMode,
    setOverlayOpacity,
    setWipe,
  };
}
